import React from 'react'
import './NavBar.scss'
import { useLocation, NavLink } from 'react-router-dom'
import { useNavBarItem } from 'hooks/useNavBarItem'
import { useParamountAuth } from 'context/ParamountAuthContext'
import { VersionInfo } from 'components'
import { checkActive } from 'common/functions'
import LogoutButton from 'components/buttons/LogoutButton'
import { useQuery } from '@tanstack/react-query'
import { getPendingCompanies, getSiteChecklistsForCompany, getPendingPrintRequestCount, getHazardReports } from 'services/clients.svc'
import { Roles } from '@paramount-prestart/shared'

const { SuperUser: ROLE_SUPER_USER, CompanyAdmin: ROLE_COMPANY_ADMIN } = Roles.ROLES

function NavBar() {
  const location = useLocation()
  const { userRoles, userCompanyId } = useParamountAuth()
  const navItems = useNavBarItem()

  if (
    location.pathname === '/' ||
    location.pathname === '/accept_invitation' ||
    location.pathname === '/unauthorised_role' ||
    location.pathname === '/self_register_company'
  ) {
    return null
  }

  const { data: pendingCount } = useQuery(
    ['pending-count'],
    () => getPendingCompanies().then(companies => companies.length),
    { enabled: Array.isArray(userRoles) && userRoles.includes(ROLE_SUPER_USER) }
  )

  const isPortalUser = Array.isArray(userRoles) && (userRoles.includes(ROLE_COMPANY_ADMIN) || userRoles.includes('CompanyUser')) && !!userCompanyId

  const { data: unreviewedChecklistCount } = useQuery(
    ['unreviewed-checklists-count', userCompanyId],
    () => getSiteChecklistsForCompany(userCompanyId).then(list => list.filter(c => !c.reviewedAt).length),
    { enabled: isPortalUser }
  )

  const { data: unreviewedHazardReportCount } = useQuery(
    ['unreviewed-hazard-reports-count', userCompanyId],
    () => getHazardReports(userCompanyId).then(list => list.filter(r => !r.reviewedAt).length),
    { enabled: isPortalUser }
  )

  const { data: qrPrintRequestsCount } = useQuery(
    ['qrPrintRequestsCount'],
    () => getPendingPrintRequestCount().then((res) => res.count),
    { enabled: Array.isArray(userRoles) && userRoles.includes(ROLE_SUPER_USER) }
  )

  const badgeCount = (slug) => {
    if (slug === 'pending') return pendingCount
    if (slug === 'checklists') return unreviewedChecklistCount
    if (slug === 'hazard-reports') return unreviewedHazardReportCount
    if (slug === 'qrPrintRequests') return qrPrintRequestsCount
    return 0
  }

  return (
    <aside className="nav-container">
      <nav>
        <ul>
          {navItems.map((item) => {
            const isAllowed = userRoles?.some((role) =>
              item.allowedRoles?.includes(role),
            )
            if (isAllowed) {
              const count = badgeCount(item.slug)
              return (
                <li key={item.name}>
                  <NavLink
                    to={item.page}
                    className={({ isActive }) =>
                      isActive ? 'active' : checkActive(item.slug, location)
                    }
                    end
                  >
                    <item.icon fill={item.fill} width={24} />
                    <p>{item.name}</p>
                    {item.showBadge && count > 0 && (
                      <span className="nav-badge">{count}</span>
                    )}
                  </NavLink>
                </li>
              )
            } else {
              return null
            }
          })}
        </ul>
      </nav>
      <div className="navbar-footer">
        <LogoutButton />
        <VersionInfo />
      </div>
    </aside>
  )
}

export default NavBar
